import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { ThermalConditionBadge } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/location/thermal-progress.response';

/** Couleur (jetons Terreau) et pictogramme de chaque badge affiché : la couleur ne porte jamais seule le sens. */
const BADGES: Partial<Record<ThermalConditionBadge, { classes: string; icon: string }>> = {
  OPTIMAL: { classes: 'text-vw-primary-ink bg-vw-surface-tint', icon: '↑' },
  RALENTI_FROID: { classes: 'text-vw-cold bg-vw-cold-bg', icon: '↘' },
  RALENTI_CHALEUR: { classes: 'text-vw-warn-ink bg-vw-warn-bg', icon: '↘' },
  PAUSE_FROID: { classes: 'text-vw-on-color bg-vw-cold', icon: '‖' },
  PAUSE_CHALEUR: { classes: 'text-vw-on-color bg-vw-danger', icon: '‖' },
  // Neutre : la plante ne va pas mal, c'est la météo qui manque.
  MISSING_DATA: { classes: 'text-vw-muted bg-vw-surface-sunk', icon: '?' },
};

/** Les badges qui s'affichent, chacun avec son libellé `gdd.badge.<badge>`. */
export const DISPLAYED_BADGES = Object.keys(BADGES) as ThermalConditionBadge[];

/**
 * Le badge à afficher pour une valeur de l'API, `null` s'il n'y en a pas :
 * étape au calendrier (`NORMAL`), absence de données, ou valeur inconnue
 * (ancienne API : FAVORABLE, UNFAVORABLE).
 */
export function displayedBadge(value: string | null | undefined): ThermalConditionBadge | null {
  return value && value in BADGES ? (value as ThermalConditionBadge) : null;
}

/** Ralenti ou en pause : la plante est à surveiller. Les pauses d'abord (0), puis les ralentis (1), le reste après (2). */
export function badgeAttentionRank(badge: ThermalConditionBadge | null | undefined): number {
  if (badge === 'PAUSE_FROID' || badge === 'PAUSE_CHALEUR') return 0;
  if (badge === 'RALENTI_FROID' || badge === 'RALENTI_CHALEUR') return 1;
  return 2;
}

@Component({
  selector: 'vw-gdd-badge',
  standalone: true,
  imports: [CommonModule, TranslatePipe],
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    @if (badge) {
      <span class="inline-flex items-center gap-0.5 text-[10px] font-medium px-1.5 py-0.5 rounded-full"
            [ngClass]="badge.classes">
        {{ badge.icon }} {{ 'gdd.badge.' + conditionBadge | translate }}
      </span>
    }
  `,
})
export class GddBadgeComponent {
  @Input() conditionBadge: string | null = null;

  /** Couleur et pictogramme ; rien pour une étape au calendrier ou une valeur inconnue. */
  get badge(): { classes: string; icon: string } | null {
    const badge = displayedBadge(this.conditionBadge);
    return badge ? BADGES[badge]! : null;
  }
}
